<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">资金申请</h1>
            <p class="list-page-description">提交注资、增资、减资、撤资、续期申请，并查看审批与落账状态。</p>
          </div>
          <div class="list-page-heading-actions">
            <button v-permission="'wealth:fund:invest'" type="button" class="btn-primary" @click="openApply('INVEST')">提交注资</button>
            <button v-permission="'wealth:fund:increase'" type="button" class="btn-secondary" @click="openApply('INCREASE')">提交增资</button>
            <button v-permission="'wealth:fund:decrease'" type="button" class="btn-secondary" @click="openApply('DECREASE')">提交减资</button>
            <button v-permission="'wealth:fund:withdraw'" type="button" class="btn-secondary" @click="openApply('WITHDRAW')">提交撤资</button>
            <button v-permission="'wealth:fund:rollover'" type="button" class="btn-secondary" @click="openApply('ROLLOVER')">提交续期</button>
          </div>
        </div>
      </div>
    </template>

    <div class="list-page-table-panel mx-6">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="loadApplies"
      >
        <input v-model="filters.applyNo" class="form-input form-control-sm filter-bar-field" placeholder="请输入申请编号">
        <input v-model.number="filters.wealthCustomerId" type="number" class="form-input form-control-sm filter-bar-field" placeholder="请输入客户ID">
        <input v-model.number="filters.contractId" type="number" class="form-input form-control-sm filter-bar-field" placeholder="请输入合同ID">
        <el-select v-model="filters.operationType" placeholder="请选择类型" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部类型" value="" />
          <el-option v-for="item in operationTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-select v-model="filters.status" placeholder="请选择状态" class="form-select form-control-sm filter-bar-field">
          <el-option label="全部状态" value="" />
          <el-option v-for="item in applyStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">申请</th>
                <th class="data-table-th text-left">类型</th>
                <th class="data-table-th text-left">客户/合同</th>
                <th class="data-table-th text-right">金额</th>
                <th class="data-table-th text-left">状态</th>
                <th class="data-table-th text-left">申请人</th>
                <th class="data-table-th text-left">申请时间</th>
                <th class="data-table-th text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="data-table-empty">加载中...</td></tr>
              <tr v-else-if="rows.length === 0"><td colspan="8" class="data-table-empty">暂无申请</td></tr>
              <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
                <td class="data-table-td">
                  <div class="font-mono text-xs text-slate-900">{{ row.applyNo }}</div>
                  <div class="mt-1 flex items-center gap-2 text-xs text-slate-500">
                    <span>实例 {{ row.approvalInstanceId || '-' }}</span>
                    <button
                      v-if="row.approvalInstanceId"
                      type="button"
                      class="inline-approval-link rounded px-1 text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                      @click.stop="openApprovalDetail(row)"
                    >
                      审批详情
                    </button>
                  </div>
                </td>
                <td class="data-table-td"><WealthStatusBadge type="operation" :value="row.operationType" /></td>
                <td class="data-table-td"><div>客户 {{ row.wealthCustomerId || '-' }}</div><div class="text-xs text-slate-500">合同 {{ row.contractId || '-' }}</div></td>
                <td class="data-table-td is-right money-text">¥{{ formatMoney(row.rolloverPrincipalAmount || row.amount) }}</td>
                <td class="data-table-td"><WealthStatusBadge type="apply" :value="row.status" show-dot /></td>
                <td class="data-table-td">{{ row.applicantName || row.applicantUserId || '-' }}</td>
                <td class="data-table-td text-xs">{{ formatDateTime(row.applyTime) }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button class="table-action-button is-primary" title="详情" aria-label="详情" @click="openDetail(row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    </button>
                    <button v-if="row.approvalInstanceId" class="table-action-button is-purple" title="查看审批详情" aria-label="查看审批详情" @click="openApprovalDetail(row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                      </svg>
                    </button>
                    <button v-if="row.status === 'APPROVING'" v-permission="'wealth:operation-apply:cancel'" class="table-action-button is-danger" title="撤回" aria-label="撤回" @click="cancelApply(row)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <AppPagination v-if="pagination.total > 0" class="pagination-bridge" :pagination="pagination" :page-sizes="PAGE_SIZE_OPTIONS" @page-change="handlePageChange" @size-change="handleSizeChange" />
    </div>

    <WealthOperationApplyModal v-model="applyVisible" :operation-type="applyType" @submitted="loadApplies" />

    <WealthDetailDrawer v-model="detailVisible" title="资金申请详情" :subtitle="selectedApply?.applyNo">
      <div v-if="selectedApply" class="space-y-5">
        <div class="flex justify-end">
          <button
            v-if="selectedApply.approvalInstanceId"
            type="button"
            class="btn-secondary inline-flex items-center gap-1.5"
            @click="openApprovalDetail(selectedApply)"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            查看审批详情
          </button>
        </div>
        <section class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
          <div><div class="text-xs text-slate-500">操作类型</div><WealthStatusBadge type="operation" :value="selectedApply.operationType" /></div>
          <div><div class="text-xs text-slate-500">状态</div><WealthStatusBadge type="apply" :value="selectedApply.status" /></div>
          <div><div class="text-xs text-slate-500">申请金额</div><div class="money-text">¥{{ formatMoney(selectedApply.amount) }}</div></div>
          <div><div class="text-xs text-slate-500">续期本金</div><div class="money-text">¥{{ formatMoney(selectedApply.rolloverPrincipalAmount) }}</div></div>
          <div><div class="text-xs text-slate-500">客户ID</div><div>{{ selectedApply.wealthCustomerId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">合同ID</div><div>{{ selectedApply.contractId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">审批实例</div><div>{{ selectedApply.approvalInstanceId || '-' }}</div></div>
          <div><div class="text-xs text-slate-500">失败原因</div><div class="text-red-600">{{ selectedApply.accountFailedReason || selectedApply.approvalStartFailedReason || '-' }}</div></div>
        </section>
        <section class="rounded-lg border border-slate-200">
          <div class="border-b border-slate-200 px-4 py-3"><h4 class="text-sm font-semibold text-slate-900">批次明细</h4></div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head"><tr><th class="data-table-th text-left">批次</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-right">提交前本金</th><th class="data-table-th text-left">起止日期</th></tr></thead>
                <tbody>
                  <tr v-if="details.length === 0"><td colspan="4" class="data-table-empty">暂无明细</td></tr>
                  <tr v-for="item in details" v-else :key="item.id" class="data-table-row">
                    <td class="data-table-td"><div class="font-mono text-xs">{{ item.batchNoSnapshot || item.batchId }}</div></td>
                    <td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td>
                    <td class="data-table-td is-right">¥{{ formatMoney(item.beforeBatchPrincipalSnapshot) }}</td>
                    <td class="data-table-td">{{ formatDate(item.valueDateSnapshot) }} 至 {{ formatDate(item.maturityDateSnapshot) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <WealthAttachmentPanel title="申请附件" biz-type="OPERATION_APPLY" :biz-id="selectedApply.id" :can-manage="true" />
      </div>
    </WealthDetailDrawer>

    <ApprovalDetailModal
      v-model="showApprovalDetailModal"
      :instance-id="selectedApprovalInstanceId"
      :type="'wealth'"
      title="审批详情"
    />
  </ListPageShell>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { cancelWealthOperationApply, getWealthOperationApplyBatchDetails, getWealthOperationApplyPage } from '@/api/wealth'
import WealthAttachmentPanel from '../components/WealthAttachmentPanel.vue'
import WealthDetailDrawer from '../components/WealthDetailDrawer.vue'
import WealthOperationApplyModal from '../components/WealthOperationApplyModal.vue'
import WealthStatusBadge from '../components/WealthStatusBadge.vue'
import { applyStatusOptions, cleanPayload, formatDate, formatDateTime, formatMoney, operationTypeOptions, PAGE_SIZE_OPTIONS, pagePayload, syncPageResult } from '../utils'

const filters = reactive({ applyNo: '', operationType: '', wealthCustomerId: null, contractId: null, status: '' })
const pagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const rows = ref([])
const loading = ref(false)
const applyVisible = ref(false)
const applyType = ref('INVEST')
const detailVisible = ref(false)
const selectedApply = ref(null)
const details = ref([])
const showApprovalDetailModal = ref(false)
const selectedApprovalInstanceId = ref(null)

async function loadApplies() {
  loading.value = true
  try {
    const result = await getWealthOperationApplyPage(pagePayload(pagination, cleanPayload(filters)))
    rows.value = result?.records || []
    syncPageResult(pagination, result)
  } finally {
    loading.value = false
  }
}

function handleSearch() { pagination.current = 1; pagination.page = 1; loadApplies() }
function handleReset() { Object.assign(filters, { applyNo: '', operationType: '', wealthCustomerId: null, contractId: null, status: '' }); handleSearch() }
function handlePageChange(page) { pagination.current = page; pagination.page = page; loadApplies() }
function handleSizeChange(size) { pagination.size = size; handleSearch() }
function openApply(type) { applyType.value = type; applyVisible.value = true }

async function openDetail(row) {
  selectedApply.value = row
  detailVisible.value = true
  details.value = await getWealthOperationApplyBatchDetails(row.id)
}

function openApprovalDetail(row) {
  if (!row?.approvalInstanceId) {
    ElMessage.warning('暂无审批实例')
    return
  }
  selectedApprovalInstanceId.value = row.approvalInstanceId
  showApprovalDetailModal.value = true
}

async function cancelApply(row) {
  try {
    await ElMessageBox.confirm(`确认撤回申请 ${row.applyNo} 吗？`, '撤回申请', { confirmButtonText: '确认撤回', cancelButtonText: '取消', type: 'warning' })
    await cancelWealthOperationApply(row.id, {})
    ElMessage.success('申请已撤回')
    loadApplies()
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

onMounted(loadApplies)
</script>
